import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Alert, Box, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { match, P } from 'ts-pattern';

import {
  B2bCampaignPaymentMethod,
  GetB2bCampaignOneResponse,
} from '@Common/types/bo/b2b/campaigns';

import { ShowSection } from '@sh4res/bo-common/components';
import { b2bCampaignOperationPaymentMethodChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { useWatchObject } from '@sh4res/bo-common/hooks';

const testId = 'BoExternalB2bCampaignPaymentStep';

const toCampaignPaymentMethod = (
  formPaymentMethod: B2bCampaignFormValues['payment_method'] | undefined,
): B2bCampaignPaymentMethod | null =>
  match<B2bCampaignFormValues['payment_method'] | undefined, B2bCampaignPaymentMethod | null>(
    formPaymentMethod,
  )
    .with('sepa', 'sepa_linked', 'sepa_new', () => 'sepa')
    .with('transfer', () => 'transfer')
    .with(P.nullish, () => null)
    .exhaustive();

export const PaymentStep = ({
  isBoExternal,
  isOngoingConsultationEdit = false,
  distributorStepsOverride = false,
  isFundRequestPaymentMethodEdit = false,
}: {
  isBoExternal: boolean;
  isOngoingConsultationEdit?: boolean;
  distributorStepsOverride?: boolean;
  isFundRequestPaymentMethodEdit?: boolean;
}) => {
  const translate = useTranslate();
  const {
    payment_method: paymentMethod,
    choice_collected_by,
    allocation_computed_by: allocationComputedBy,
    type: campaignType,
  } = useWatchObject<B2bCampaignFormValues>()([
    'payment_method',
    'choice_collected_by',
    'allocation_computed_by',
    'type',
  ]);
  const { setValue } = useFormContext();
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  const editionDisabled = useMemo(() => {
    if (allocationComputedBy !== 'distributor') {
      return false;
    }
    if (isBoExternal) {
      return record?.status !== 'campaign_awaiting_customer_confirmation';
    }
    if (distributorStepsOverride) {
      return false;
    }

    return record?.status === 'campaign_awaiting_customer_confirmation';
  }, [record, isBoExternal, allocationComputedBy, distributorStepsOverride]);

  const isCETCampaignDaysAllocation = isCampaignVacationDaysAllocation({
    type: campaignType,
    choice_collected_by,
  });

  const fundRequestBlocker = isFundRequestPaymentMethodEdit
    ? (record?.payment_method_edit_blocker ?? null)
    : null;

  const cancelsFundRequest =
    isFundRequestPaymentMethodEdit &&
    G.isNullable(fundRequestBlocker) &&
    G.isNotNullable(record?.savings_funds_request_id) &&
    toCampaignPaymentMethod(paymentMethod) !== record.payment_method;

  useEffect(() => {
    if (paymentMethod === 'sepa') {
      setValue('payment_method', 'sepa_linked');
    }
  }, [paymentMethod, setValue]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {choice_collected_by === 'distributor' && !isCETCampaignDaysAllocation && (
        <Typography sx={{ display: 'flex', gap: 1 }}>
          <Warning />
          <span>
            {translate(
              'resources.bo_common.b2b.campaigns.sections.payment.can_not_pay_description',
            )}
          </span>
        </Typography>
      )}
      {isCETCampaignDaysAllocation && (
        <Typography>
          {translate('resources.bo_common.b2b.campaigns.sections.payment.can_pay_description')}
        </Typography>
      )}

      {G.isNotNullable(fundRequestBlocker) && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid={`${testId}_PaymentMethodLockedAlert`}>
          {translate('resources.bo_common.b2b.campaigns.sections.payment.edit_locked', {
            reason: fundRequestBlocker,
          })}
        </Alert>
      )}

      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.payment.payment_method"
        noCard={true}>
        <RadioButtonGroupInput
          data-testid={`${testId}_PaymentMethodInput`}
          source="payment_method"
          choices={b2bCampaignOperationPaymentMethodChoices}
          required
          disabled={
            G.isNotNullable(fundRequestBlocker) ||
            (editionDisabled && !isOngoingConsultationEdit && !isFundRequestPaymentMethodEdit)
          }
        />

        {/*         {paymentMethod === 'sepa_new' && !editionDisabled && (
          <FileInput
            source="upload_rib"
            label="resources.bo_common.b2b.campaigns.sections.payment.upload"
            accept=".pdf"
            isRequired
            validate={[required()]}>
            <FileField source="src" title="title" />
          </FileInput>
        )} */}
      </ShowSection>

      {cancelsFundRequest && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          data-testid={`${testId}_FundRequestCancellationAlert`}>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.payment.cancels_fund_request_warning',
            { fundRequestId: record.savings_funds_request_id },
          )}
        </Alert>
      )}

      {(paymentMethod === 'sepa_linked' || paymentMethod === 'sepa_new') && (
        <Typography variant="body2" sx={{ mt: 'auto', pt: 3, color: 'text.secondary' }}>
          {translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.checkout_disclaimer',
          )}
        </Typography>
      )}
    </Box>
  );
};
